interface CollectionProgressSource {
  totalStickers: number;
  stickersRequiredToComplete?: number | null;
  collectedCount: number;
}

/**
 * Stickers needed to complete the collection (all of them unless the creator set a number).
 * Managers get the saved number, which can be above the total; collectors complete at the total.
 */
export function requiredStickerCount(collection: CollectionProgressSource): number {
  const required = collection.stickersRequiredToComplete ?? 0;
  return required > 0 ? Math.min(required, collection.totalStickers) : collection.totalStickers;
}

/** Collected stickers counted toward completion, capped at the required number. */
export function completionProgressCount(collection: CollectionProgressSource): number {
  return Math.min(collection.collectedCount, requiredStickerCount(collection));
}

/** "3/4" style label for progress toward completion. */
export function completionProgressLabel(collection: CollectionProgressSource): string {
  return `${completionProgressCount(collection)}/${requiredStickerCount(collection)}`;
}

export function remainingToCompleteText(collection: CollectionProgressSource): string {
  const remaining = Math.max(
    0,
    requiredStickerCount(collection) - completionProgressCount(collection),
  );
  return `${remaining} more to complete the collection`;
}

/** Status line for a completed collection. */
export function completedCollectionText(collection: CollectionProgressSource): string {
  const required = requiredStickerCount(collection);
  if (required < collection.totalStickers) return 'Collection complete! 🎉';
  return collection.totalStickers === 1
    ? 'Sticker collected! 🎉'
    : `All ${collection.totalStickers} stickers collected! 🎉`;
}

/** What it takes to complete: "Collect them all…" or "Collect 2 of 5…". */
export function completionGoalText(collection: CollectionProgressSource): string {
  const required = requiredStickerCount(collection);
  if (required < collection.totalStickers) {
    return `Collect ${required} of ${collection.totalStickers} to complete the collection.`;
  }
  return 'Collect them all to complete the collection.';
}
